import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Banknote,
  Database,
  Download,
  ExternalLink,
  Globe,
  MoreHorizontal,
  Pause,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Tabs } from '../../components/Tabs';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { CashPaymentModal } from '../../components/CashPaymentModal';
import { LedgerView } from '../../components/LedgerView';
import { toMajor, toMinor } from '../../contracts/money';
import { useAdminBusinesses, useAdminBusiness, setBusinessStatus, refundPayment, type AdminBusinessDetail } from '../../api/admin';
import { useAdminBusinessLedger } from '../../api/billing';
import { openBusinessPosAsAdmin } from '../../api/pos';
import { formatDate, timeAgo } from '../../lib/format';

const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  active: 'success', trial: 'info', past_due: 'warning', suspended: 'error',
  canceled: 'neutral', pending_payment: 'warning', provisioning: 'info', deleted: 'neutral',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Active', trial: 'Trial', past_due: 'Past due', suspended: 'Suspended',
  canceled: 'Canceled', pending_payment: 'Pending pay', provisioning: 'Provisioning', deleted: 'Deleted',
};
const DB_LABEL: Record<string, string> = { managed: 'Managed DB', self_hosted: 'Self-hosted DB', legacy: 'Legacy (shared)' };

/** Business detail — header + 7 tabs (Overview, Subscription, Payments, Database, Domains, Users, Audit). */
export function BusinessDetail() {
  const { data: adminBusinesses = [] } = useAdminBusinesses();
  // Select the routed business (the page used to always show adminBusinesses[0]).
  const params = useParams({ strict: false }) as { id?: string };
  const b = adminBusinesses.find((x) => x.id === params.id);
  // Full drill-in (real DB/subscription/payments) — the list only carries the header fields.
  const { data: detail } = useAdminBusiness(params.id ?? '');
  const { data: ledger } = useAdminBusinessLedger(params.id ?? '');
  const qc = useQueryClient();
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [posBusy, setPosBusy] = useState(false);

  async function openPosAsAdmin() {
    if (!b || posBusy) return;
    setPosBusy(true);
    try {
      await openBusinessPosAsAdmin(b.id, '/home');
    } finally {
      setPosBusy(false);
    }
  }

  async function setStatus(status: Parameters<typeof setBusinessStatus>[1]) {
    const id = params.id;
    if (!id || statusBusy) return;
    setStatusBusy(true);
    setStatusError(null);
    try {
      await setBusinessStatus(id, status);
      void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'business', id] });
    } catch (e) {
      // Previously swallowed — a failed Suspend/Activate looked like a no-op. Surface it.
      setStatusError(e instanceof Error ? e.message : 'Could not change status. Please try again.');
    } finally {
      setStatusBusy(false);
    }
  }

  // ── Refund a payment (claws back reseller commission) ──────────────────────
  const refundablePayments = (detail?.payments ?? []).filter((p) => p.status === 'paid');
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundPaymentId, setRefundPaymentId] = useState('');
  const [refundAmount, setRefundAmount] = useState(''); // major units (৳)
  const [refundReason, setRefundReason] = useState('');
  const [refundChannel, setRefundChannel] = useState<'refund' | 'chargeback'>('refund');
  const [refundError, setRefundError] = useState<string | null>(null);
  const refundTarget = refundablePayments.find((p) => p.id === refundPaymentId);

  function openRefund() {
    const target = refundablePayments[0];
    if (!target) return;
    setRefundPaymentId(target.id);
    setRefundAmount(String(toMajor(target.amountMinor)));
    setRefundReason('');
    setRefundChannel('refund');
    setRefundError(null);
    setRefundOpen(true);
  }

  async function doRefund() {
    const id = params.id;
    const amountMinor = toMinor(Number(refundAmount));
    if (!refundPaymentId || !Number.isFinite(amountMinor) || amountMinor <= 0) {
      setRefundError('Enter a valid amount.');
      return;
    }
    setRefundBusy(true);
    setRefundError(null);
    try {
      await refundPayment(refundPaymentId, {
        amountMinor,
        reason: refundReason.trim() || undefined,
        channel: refundChannel,
      });
      if (id) void qc.invalidateQueries({ queryKey: ['admin', 'business', id] });
      void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
      setRefundOpen(false);
    } catch (e) {
      setRefundError(e instanceof Error ? e.message : 'Refund failed.');
    } finally {
      setRefundBusy(false);
    }
  }

  if (adminBusinesses.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }
  if (!b) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Business not found.
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link
        to="/admin/businesses"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        All businesses
      </Link>

      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <span className="w-12 h-12 rounded-lg bg-zinc-950 dark:bg-zinc-50 grid place-items-center text-base font-semibold text-white dark:text-zinc-950 flex-shrink-0">
              {b.name.split(' ').slice(0, 2).map((p) => p.charAt(0)).join('')}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 truncate">
                  {b.name}
                </h1>
                <Badge tone={STATUS_TONE[b.status] ?? 'neutral'}>{STATUS_LABEL[b.status] ?? b.status}</Badge>
                {b.paymentReviewAt && <Badge tone="warning">Review</Badge>}
                <span className="text-xs text-zinc-500 dark:text-zinc-400">·</span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">{b.plan} · {DB_LABEL[b.dbMode] ?? b.dbMode}</span>
              </div>
              <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
                Reseller: {b.resellerName ?? 'Direct'} · Owner: {b.ownerEmail} · <span className="mono">business_id: {b.id.replace('biz_', '')}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" size="sm" iconLeft={<ExternalLink className="w-4 h-4" />} loading={posBusy} onClick={openPosAsAdmin}>
              Open POS
            </Button>
            <Button size="sm" iconLeft={<Banknote className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record payment
            </Button>
            {b.status === 'suspended' ? (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy} onClick={() => setStatus('active')}>
                Activate
              </Button>
            ) : (
              <Button variant="secondary" size="sm" iconLeft={<Pause className="w-4 h-4" />} disabled={statusBusy} onClick={() => setStatus('suspended')}>
                Suspend
              </Button>
            )}
            <Button variant="secondary" size="sm" iconLeft={<XCircle className="w-4 h-4" />} disabled={statusBusy || b.status === 'canceled'} onClick={() => setStatus('canceled')}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={refundablePayments.length === 0}
              title={refundablePayments.length === 0 ? 'No refundable (paid) payments' : undefined}
              onClick={openRefund}
            >
              Refund
            </Button>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {statusError && (
          <p className="mt-3 text-sm text-rose-600 dark:text-rose-400 lg:text-right">{statusError}</p>
        )}
      </Card>

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview', content: <OverviewTab detail={detail} /> },
            { id: 'subscription', label: 'Subscription', content: <SubscriptionTab sub={detail?.subscription ?? null} /> },
            { id: 'payments', label: 'Payments', content: <PaymentsTab payments={detail?.payments ?? []} /> },
            { id: 'ledger', label: 'Ledger', count: ledger?.rows.length, content: <LedgerView ledger={ledger} /> },
            { id: 'database', label: 'Database', content: <DatabaseTab db={detail?.database ?? null} /> },
            { id: 'domains', label: 'Domains', content: <DomainsTab domains={detail?.domains ?? []} /> },
            { id: 'users', label: 'Users', content: <UsersTab users={detail?.users ?? []} /> },
            { id: 'audit', label: 'Audit', content: <AuditTab audit={detail?.audit ?? []} /> },
          ]}
        />
      </Card>

      <CashPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        businessId={b.id}
        businessName={b.name}
        onRecorded={() => {
          if (params.id) void qc.invalidateQueries({ queryKey: ['admin', 'business', params.id] });
          void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
          void qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
        }}
      />

      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title="Issue refund"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRefundOpen(false)} disabled={refundBusy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={doRefund} loading={refundBusy}>
              {refundChannel === 'chargeback' ? 'Record chargeback' : 'Issue refund'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {refundTarget && (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">
              Refunding{' '}
              <span className="font-medium text-zinc-900 dark:text-zinc-100 mono">{refundTarget.invoiceNo}</span>{' '}
              (<Money minor={refundTarget.amountMinor} hideCents tone="neutral" /> · {refundTarget.method}). This claws
              back the reseller commission for this payment.
            </p>
          )}
          <Input label="Amount (৳)" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
          <Input
            label="Reason (optional)"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="e.g. duplicate charge"
          />
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Channel</span>
            <div className="flex gap-2">
              {(['refund', 'chargeback'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setRefundChannel(c)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition capitalize ${
                    refundChannel === c
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          {refundError && <p className="text-sm text-rose-600 dark:text-rose-400">{refundError}</p>}
        </div>
      </Modal>
    </div>
  );
}

function OverviewTab({ detail }: { detail: AdminBusinessDetail | undefined }) {
  const sub = detail?.subscription;
  const db = detail?.database;
  const audit = detail?.audit ?? [];
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Subscription" />
          {sub ? (
            <dl className="space-y-2.5 text-sm">
              <Row k="Plan" v={`${sub.plan ?? '—'} · ${taka(sub.unitAmountMinor)}/${sub.interval}`} />
              <Row k="Status" v={<Badge tone={sub.status === 'active' ? 'success' : sub.status === 'trial' ? 'info' : sub.status === 'past_due' ? 'warning' : 'error'}>{sub.status}</Badge>} />
              <Row k="Renews" v={<span className="tabular-nums">{sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'}</span>} />
            </dl>
          ) : (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">No subscription on record.</p>
          )}
        </Card>

        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <CardHeader title="Database" />
          {db ? (
            <dl className="space-y-2.5 text-sm">
              <Row k="Mode" v={db.mode === 'self_hosted' ? 'Self-hosted' : 'Managed'} />
              <Row k="Status" v={db.status} />
              <Row k="Host" v={<span className="mono text-xs">{db.host}:{db.port}</span>} />
              <Row k="DB name" v={<span className="mono text-xs">{db.dbName}</span>} />
            </dl>
          ) : (
            <p className="text-sm text-zinc-600 dark:text-zinc-300">Shared legacy database (no dedicated DB).</p>
          )}
        </Card>
      </div>

      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Recent activity" />
        {audit.length === 0 ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No recent activity recorded.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {audit.map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400 w-20 flex-shrink-0 pt-0.5">{a.occurredAt ? formatDate(a.occurredAt) : '—'}</span>
                <span className="text-zinc-700 dark:text-zinc-200"><span className="mono text-xs">{a.action}</span> · {a.category}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SubscriptionTab({ sub }: { sub: AdminBusinessDetail['subscription'] }) {
  if (!sub) {
    return (
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Subscription" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">No subscription on record for this business.</p>
      </Card>
    );
  }
  const tone =
    sub.status === 'active' ? 'success' : sub.status === 'trial' ? 'info' : sub.status === 'past_due' ? 'warning' : 'error';
  const taka = (m: number) => '৳' + (m / 100).toLocaleString('en-US');
  return (
    <div className="space-y-4">
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Current plan" subtitle={`${sub.plan ?? '—'} · ${sub.interval}`} action={<Badge tone={tone}>{sub.status}</Badge>} />
        <div className="grid grid-cols-3 gap-4">
          <Stat label="Price" v={taka(sub.unitAmountMinor)} />
          <Stat label="Interval" v={sub.interval} />
          <Stat label="Renews" v={sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'} />
        </div>
        {sub.trialEndsAt && (
          <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">Trial ends {formatDate(sub.trialEndsAt)}</div>
        )}
        <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap gap-2">
          <Button variant="secondary" size="sm">Change plan</Button>
          <Button variant="ghost" size="sm">Force renew</Button>
          <Button variant="ghost" size="sm">Cancel</Button>
        </div>
      </Card>
    </div>
  );
}

const PAY_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  paid: 'success', pending: 'warning', failed: 'error', refunded: 'neutral', partial_refunded: 'neutral', disputed: 'error',
};
const PAY_METHOD: Record<string, string> = {
  sslcommerz: 'SSLCommerz', manual_cash: 'Cash', bank_transfer: 'Bank Transfer', bkash: 'bKash', nagad: 'Nagad', stripe: 'Stripe', paypal: 'PayPal',
};

function PaymentsTab({ payments }: { payments: AdminBusinessDetail['payments'] }) {
  if (payments.length === 0) {
    return <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">No payments recorded for this business yet.</div>;
  }
  return (
    <div className="overflow-x-auto nice-scroll -mx-6">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">Invoice</th>
            <th className="text-left font-medium px-4 py-3">Date</th>
            <th className="text-right font-medium px-4 py-3">Amount</th>
            <th className="text-left font-medium px-4 py-3">Method</th>
            <th className="text-left font-medium px-6 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {payments.map((r) => (
            <tr key={r.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
              <td className="px-6 py-3 mono text-xs text-zinc-700 dark:text-zinc-200">{r.invoiceNo}</td>
              <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{r.paidAt ? formatDate(r.paidAt) : '—'}</td>
              <td className="px-4 py-3 text-right">
                <Money minor={r.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
              </td>
              <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{PAY_METHOD[r.method] ?? r.method}</td>
              <td className="px-6 py-3">
                <Badge tone={PAY_TONE[r.status] ?? 'neutral'}>{r.status.replace(/_/g, ' ')}</Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DatabaseTab({ db }: { db: AdminBusinessDetail['database'] }) {
  if (!db) {
    return (
      <Card className="p-5 border-zinc-200 dark:border-zinc-700">
        <CardHeader title="Database" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          No dedicated database — this business runs on the shared legacy database (grandfathered). A dedicated DB is
          provisioned only for new managed businesses, or registered for self-hosted (BYO-DB).
        </p>
      </Card>
    );
  }
  const tone = db.status === 'ready' ? 'success' : db.status === 'failed' ? 'error' : 'warning';
  return (
    <Card className="p-5 border-zinc-200 dark:border-zinc-700">
      <CardHeader
        title={db.mode === 'self_hosted' ? 'Self-hosted database' : 'Managed database'}
        subtitle={db.provisionedAt ? `Provisioned ${formatDate(db.provisionedAt)}` : 'Not provisioned yet'}
        action={<Badge tone={tone}>{db.status}</Badge>}
      />
      <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
        <Row k="Mode" v={db.mode === 'self_hosted' ? 'Self-hosted (BYO)' : 'Managed'} />
        <Row k="Status" v={db.status} />
        <Row k="Host" v={<span className="mono text-xs">{db.host}:{db.port}</span>} />
        <Row k="Database" v={<span className="mono text-xs">{db.dbName}</span>} />
        <Row k="Provisioned" v={db.provisionedAt ? formatDate(db.provisionedAt) : '—'} />
        <Row k="Attempts" v={String(db.attemptCount)} />
        {db.lastError && <Row k="Last error" v={<span className="text-red-600 dark:text-red-400 text-xs">{db.lastError}</span>} />}
      </dl>
      <div className="mt-5 pt-5 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" iconLeft={<Download className="w-3.5 h-3.5" />}>Backup now</Button>
        <Button variant="secondary" size="sm" iconLeft={<RefreshCw className="w-3.5 h-3.5" />}>Restart connection pool</Button>
        <Button variant="ghost" size="sm">Migrate to self-host</Button>
      </div>
    </Card>
  );
}

function DomainsTab({ domains }: { domains: AdminBusinessDetail['domains'] }) {
  if (domains.length === 0) {
    return (
      <div className="space-y-3">
        <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">
          No custom domains for this business — it uses the platform / reseller domain.
        </div>
        <Link to="/admin/domains">
          <Button variant="secondary" size="sm">Manage domains</Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {domains.map((d) => (
        <Card key={d.host} className="p-4 sm:p-5 border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500"><Globe className="w-4 h-4" /></span>
              <div>
                <div className="font-medium text-zinc-950 dark:text-zinc-50 flex items-center gap-2">
                  {d.host}
                  {d.isPrimary && <span className="text-[10px] uppercase tracking-wide bg-zinc-950 dark:bg-zinc-50 text-white dark:text-zinc-950 px-1.5 py-px rounded">Primary</span>}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">DNS {d.dnsStatus} · TLS {d.tlsStatus}</div>
              </div>
            </div>
            <Badge tone={d.dnsStatus === 'verified' ? 'success' : d.dnsStatus === 'failed' ? 'error' : 'warning'}>
              {d.dnsStatus === 'verified' ? 'Verified' : d.dnsStatus === 'failed' ? 'Failed' : 'Pending'}
            </Badge>
          </div>
        </Card>
      ))}
      <Link to="/admin/domains">
        <Button variant="secondary" size="sm">Manage domains</Button>
      </Link>
    </div>
  );
}

function UsersTab({ users }: { users: AdminBusinessDetail['users'] }) {
  if (users.length === 0) {
    return <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">No users linked to this business.</div>;
  }
  return (
    <div className="overflow-x-auto nice-scroll -mx-6">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">User</th>
            <th className="text-left font-medium px-4 py-3">Role</th>
            <th className="text-left font-medium px-6 py-3">Joined</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {users.map((u, i) => (
            <tr key={`${u.email}-${i}`} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
              <td className="px-6 py-3">
                <div className="font-medium text-zinc-950 dark:text-zinc-50">{u.name}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                  {u.username ? <span className="font-mono text-zinc-600 dark:text-zinc-300">@{u.username}</span> : null}
                  {u.username && u.email && u.email !== u.username ? <span className="mx-1 text-zinc-300 dark:text-zinc-600">·</span> : null}
                  {u.email && u.email !== u.username ? u.email : null}
                  {!u.username && !u.email ? '—' : null}
                </div>
              </td>
              <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 mono text-xs">{u.role}</td>
              <td className="px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{u.joinedAt ? formatDate(u.joinedAt) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({ audit }: { audit: AdminBusinessDetail['audit'] }) {
  if (audit.length === 0) {
    return <div className="px-2 py-6 text-sm text-zinc-500 dark:text-zinc-400">No audit events recorded for this business yet.</div>;
  }
  return (
    <ul className="space-y-3">
      {audit.map((a) => (
        <li key={a.id} className="flex items-start gap-3 text-sm">
          <span className="w-7 h-7 grid place-items-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 flex-shrink-0 mt-0.5">
            <Database className="w-3.5 h-3.5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-zinc-900 dark:text-zinc-100">
              <span className="font-medium mono text-xs">{a.action}</span>
              <span className="text-zinc-500 dark:text-zinc-400"> · {a.category}</span>
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              {a.actorEmail} · {a.occurredAt ? timeAgo(a.occurredAt) : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <dt className="text-zinc-500 dark:text-zinc-400 w-28 flex-shrink-0">{k}</dt>
      <dd className="text-zinc-900 dark:text-zinc-100 flex-1 min-w-0">{v}</dd>
    </div>
  );
}

function Stat({ label, v }: { label: string; v: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="text-base font-semibold text-zinc-950 dark:text-zinc-50 mt-0.5 tabular-nums">{v}</div>
    </div>
  );
}
